import * as Colyseus from 'colyseus.js';

import { EventEmitter } from './EventEmitter';

/**
 * @constant
 * @type {string}
 * @default
 */
const DEFAULT_SERVER = 'wss://us-lax-a1f3e564.colyseus.cloud';

/**
 * @constant
 * @type {string}
 * @default
 */
const DEFAULT_ROOM_TYPE = 'gin_rummy_room';

/**
 * @constant
 * @type {string}
 * @default
 */
const LOBBY_NAME = 'lobby';

let instance;

let reconnectionToken;

/**
 * Class for Multiplayer implementation
 * @extends EventEmitter
 */
class Multiplayer extends EventEmitter {
	static Events = {
		CHAT_RECEIVED: 'CHAT_RECEIVED',
		PLAYER_JOINED: 'PLAYER_JOINED',
	};
	rooms = [];
	gameRooms = [];
	isInitialized = false;

	/**
	 * @type {Colyseus.Client}
	 */
	client;

	/**
	 * 
	 * @param {Object} options 
	 * @param {boolean} [options.joinChat=true] - Automatically join the chat, Default true
	 * @param {boolean} [options.joinLobby=true] - Automatically join the lobby, Default true
	 * @param {string} [options.url=DEFAULT_SERVER] - URL of server
	 * @returns 
	 */
	constructor(options) {
		super();

		if (instance) {
			return instance;
		}

		this.options = options;

		instance = this;

		try {
			this.client = new Colyseus.Client(options?.colyseusServer ?? DEFAULT_SERVER);
			console.log('colyseus client', this.client);
		} catch (er) {
			console.error('Error joining multiplayer server', er);
			return;
		}

		//TODO finish chat and lobby implementation. Right now, these aren't working because they check for initilization before joining
		if (options?.joinChat ?? true) {
			this.joinChat(options?.chatRoom);
		}

		if (options?.joinLobby ?? true) {
			this.joinLobby();
		}

		this.isInitialized = true;
	}

	async joinLobby() {
		if (!this.isInitialized) {
			return;
		}

		try {
			const lobby = await this.client.joinOrCreate(LOBBY_NAME);
			lobby.onMessage('rooms', (rooms) => {
				this.rooms = rooms;
			});

			lobby.onMessage('+', ([roomId, room]) => {
				const roomIndex = this.rooms.findIndex((room) => room.roomId === roomId);

				if (roomIndex !== -1) {
					this.rooms[roomIndex] = room;

				} else {
					this.rooms.push(room);
				}

				this.rooms = Array.from(this.rooms);
			});

			lobby.onMessage('-', (roomId) => {
				this.rooms = Array.from(this.rooms.filter((room) => room.roomId !== roomId));
			});

			this.lobby = lobby;
		} catch (err) {
			console.log('failed to join lobby', err);
		}
	}

	async joinChat(chatRoomName = 'chat') {
		if (!this.isInitialized) {
			return;
		}

		try {
			this.chat = await this.client.joinOrCreate(chatRoomName);
			this.chat.onMessage('*', (type, message) => {
				this.emit(Multiplayer.Events.CHAT_RECEIVED, message);
			});
		} catch (err) {
			console.log('failed to join chat', err);
		}
	}

	async joinById(roomId, options = undefined) {
		if (!this.isInitialized) {
			return;
		}

		const result = await this.client.joinById(roomId, options);
		this.#handleRoom(result);
		return result;
	}

	async joinOrCreate(roomId = undefined, options = {}, roomType = DEFAULT_ROOM_TYPE) {
		if (!this.isInitialized) {
			return;
		}

		const opts = {
			...options,
		};

		if (roomId) {
			try {
				//If we are sent a room ID, we should first try to join it directly
				const result = await this.joinById(roomId, options);

				if (result) {
					return result;
				}

			} catch {
				//squelch error as it just means that we couldn't, room probably doesn't exist yet
			}

			opts.roomName = roomId;
		}

		const result = await this.client.joinOrCreate(roomType, opts);
		this.#handleRoom(result);
		return result;
	}

	async joinPublicGame(options = {}) {
		if (!this.isInitialized) {
			return;
		}

		let roomToUse = null;

		if (reconnectionToken) {
			try {
				const room = await this.client.reconnect(reconnectionToken);
				console.log('room result from reconnecting', room);
				this.#handleRoom(room);
				return;
			} catch (e) {
				console.log('Error rejoining room', e);
			}
		}

		if (!roomToUse) {
			const rooms = await this.client.getAvailableRooms();

			for (const room of rooms) {
				if (room.metadata?.openGame) {
					roomToUse = room;
					break;
				}
			}
		}

		if (!roomToUse) {
			roomToUse = this.createRoom({
				...options,
				private: false,
				metadata: {
					openGame: true,
				},
			});
			return roomToUse;
		}

		console.log('going to use this room to join', roomToUse);

		return this.joinById(roomToUse.roomId, options);
	}

	async createRoom(options = undefined, roomType = DEFAULT_ROOM_TYPE) {
		if (!this.isInitialized) {
			return;
		}

		const result = await this.client.create(roomType, options);
		this.#handleRoom(result);
		return result;
	}

	getRoomById(roomId) {
		let room = this.rooms.find((room) => room.roomId === roomId);

		if (!room) {
			room = this.gameRooms.find((room) => room.roomId === roomId);
		}

		return room;
	}

	async leaveGameRooms() {
		const promises = [];

		for (const room of this.gameRooms) {
			promises.push(room.leave());
		}

		this.gameRooms = [];
		return Promise.all(promises);
	}

	/**
	 * 
	 * @param {Colyseus.Room} room 
	 */
	#handleRoom(room) {
		// reconnectionToken = room.reconnectionToken;
		this.gameRooms.push(room);
	}
}

export default Multiplayer;
